import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

/**
 * Recovery for lazily-loaded chunks that no longer exist.
 *
 * Asset filenames are content-hashed, so a deploy replaces every chunk. A tab
 * that was opened before the deploy is still holding the previous
 * `index.html`, and the moment it navigates to a route it has not loaded yet
 * it asks for a hash that is gone. The browser reports that as
 * "Failed to fetch dynamically imported module", and Vite's preload helper
 * reports the stylesheet half of it as "Unable to preload CSS for ...".
 *
 * Neither is a code fault and neither is actionable — the fix is to pick up
 * the new `index.html`, which a reload does.
 */

const RELOAD_KEY = "rb:chunk-reload-at";

/**
 * A reload only helps if it fetches a newer `index.html`. If a chunk is still
 * missing immediately after one, the deploy itself is broken and reloading
 * again would spin, so wait this long before being willing to try again. Any
 * failure inside the window is left to propagate and be reported.
 */
const RELOAD_COOLDOWN_MS = 10_000;

let reloadInFlight = false;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /unable to preload css/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

function readLastReload(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    // Storage can throw in private browsing modes. Treat as "never reloaded".
    return 0;
  }
}

function writeLastReload(at: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(at));
  } catch {
    // Best effort only; the in-memory guard still prevents a tight loop.
  }
}

/**
 * Reload to pick up the current `index.html`.
 *
 * Returns whether a reload is happening, i.e. whether the caller can treat the
 * error as handled. `false` means we declined (we just reloaded and it did not
 * help) and the caller should let the error propagate to Sentry.
 */
export function selfHealReload(): boolean {
  if (reloadInFlight) return true;

  const now = Date.now();
  if (now - readLastReload() < RELOAD_COOLDOWN_MS) return false;

  reloadInFlight = true;
  writeLastReload(now);
  window.location.reload();

  return true;
}

/**
 * `React.lazy`, but a missing chunk reloads the page instead of blowing up the
 * route.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error) || !selfHealReload()) throw error;

      // A reload is in flight. Never settle, so React keeps the Suspense
      // fallback up rather than rendering against a module that failed to
      // load.
      return new Promise<never>(() => {});
    }
  });
}

/**
 * Catch-all for dynamic imports that do not go through `lazyWithRetry`.
 *
 * Vite dispatches `vite:preloadError` from its preload helper and then
 * rethrows unless the event is cancelled. We deliberately leave it
 * uncancelled: cancelling makes the helper resolve to `undefined`, which turns
 * a clear chunk error into an obscure one downstream. `lazyWithRetry` sees the
 * rethrow, finds `reloadInFlight` already set, and stays quiet.
 */
export function installChunkReloadHandler(): void {
  window.addEventListener("vite:preloadError", () => {
    selfHealReload();
  });
}
